import { useEffect, useState } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { msalInstance, LOGIN_SCOPES } from './msalConfig';
import { setAuthTokenProvider } from '../api/client';

// ── Auth gate — wraps the entire app ─────────────────────────────────────────
// Shows a login screen until the user is authenticated with a @ibewlocal1.org account.

function AuthGate({ children }: { children: React.ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [tokenReady, setTokenReady] = useState(false);

  // Once authenticated, acquire a silent token and give it to the API client
  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) return;

    const account = instance.getAllAccounts()[0];
    if (!account) return;

    instance
      .acquireTokenSilent({ scopes: LOGIN_SCOPES, account })
      .then((_result) => {
        // Provide a token getter to the API client — refreshes automatically
        setAuthTokenProvider(async () => {
          const r = await instance.acquireTokenSilent({ scopes: LOGIN_SCOPES, account });
          return r.accessToken;
        });
        setTokenReady(true);
      })
      .catch(() => {
        // Silent acquisition failed — force interactive login
        instance.acquireTokenRedirect({ scopes: LOGIN_SCOPES });
      });
  }, [isAuthenticated, inProgress, instance]);

  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen message="Signing in…" />;
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  if (!tokenReady) {
    return <LoadingScreen message="Loading…" />;
  }

  return <>{children}</>;
}

function LoginScreen() {
  const { instance } = useMsal();
  const [error, setError] = useState<string | null>(null);

  async function handleLogin() {
    setError(null);
    try {
      await instance.loginRedirect({ scopes: LOGIN_SCOPES });
    } catch (e: any) {
      setError(e?.message || String(e));
    }
  }

  return (
    <div style={styles.loginPage}>
      <div style={styles.loginCard}>
        <div style={styles.loginLogo}>📬</div>
        <h1 style={styles.loginTitle}>Outlook Audit</h1>
        <p style={styles.loginSubtitle}>
          Sign in with your IBEW Local 1 account to access the email audit timeline.
        </p>
        <button style={styles.loginBtn} onClick={handleLogin}>
          <MicrosoftIcon />
          Sign in with Microsoft
        </button>
        {error && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 12, wordBreak: 'break-word' }}>{error}</p>}
        <p style={styles.loginNote}>
          Only <strong>@ibewlocal1.org</strong> accounts are authorized.
        </p>
      </div>
    </div>
  );
}

function LoadingScreen({ message }: { message: string }) {
  return (
    <div style={styles.loginPage}>
      <div style={styles.loginCard}>
        <div style={styles.spinner} />
        <p style={{ color: '#6b7280', marginTop: 16 }}>{message}</p>
      </div>
    </div>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" style={{ marginRight: 10, flexShrink: 0 }}>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

// ── Root provider ─────────────────────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthGate>{children}</AuthGate>
    </MsalProvider>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loginPage: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', background: '#f3f4f6', fontFamily: 'system-ui, sans-serif',
  },
  loginCard: {
    background: '#fff', borderRadius: 12, padding: '48px 40px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center',
    maxWidth: 400, width: '90%',
  },
  loginLogo: { fontSize: 48, marginBottom: 16 },
  loginTitle: { fontSize: 24, fontWeight: 700, margin: '0 0 8px', color: '#111827' },
  loginSubtitle: { fontSize: 14, color: '#6b7280', margin: '0 0 28px', lineHeight: 1.6 },
  loginBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', padding: '12px 20px',
    background: '#0078d4', color: '#fff', border: 'none',
    borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
    transition: 'background 0.15s',
  },
  loginNote: { fontSize: 12, color: '#9ca3af', marginTop: 16 },
  spinner: {
    width: 32, height: 32, border: '3px solid #e5e7eb',
    borderTop: '3px solid #0078d4', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite', margin: '0 auto',
  },
};
